import { useQuery } from '@tanstack/react-query';
import { formatClientNumbers } from '@wally-reseller/shared';
import { Button } from '@wally-reseller/ui/button';
import { EmptyState, ErrorState } from '@wally-reseller/ui/empty-state';
import { Skeleton } from '@wally-reseller/ui/badge';
import { UserPlus, Users } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { useFormatDate, useFormatMoney, useT } from '@/lib/i18n';
import { StatusBadge } from './StatusBadge';

export function LeadsPage() {
  const t = useT();
  const fmt = useFormatDate();
  const money = useFormatMoney();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['registrations'], queryFn: api.registrations });

  if (list.isError) return <ErrorState error={list.error} onRetry={() => list.refetch()} />;
  if (list.isPending) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">{t('portal.leads.title')}</h1>
        <Button asChild>
          <Link to="/inscrire">
            <UserPlus />
            {t('portal.nav.register')}
          </Link>
        </Button>
      </div>
      {list.data.length === 0 ? (
        <EmptyState
          icon={Users}
          title={t('portal.leads.empty')}
          className="rounded-lg border border-dashed border-border bg-background"
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-background">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">{t('portal.leads.col.name')}</th>
                <th className="px-4 py-2 font-medium">{t('portal.leads.col.number')}</th>
                <th className="px-4 py-2 font-medium">{t('portal.leads.col.status')}</th>
                <th className="px-4 py-2 font-medium max-sm:hidden">
                  {t('portal.leads.col.submitted')}
                </th>
                <th className="px-4 py-2 font-medium max-md:hidden">
                  {t('portal.leads.col.signed')}
                </th>
                <th className="px-4 py-2 font-medium max-lg:hidden">
                  {t('portal.leads.col.lastPayment')}
                </th>
                <th className="px-4 py-2 text-right font-medium">
                  {t('portal.leads.col.commission')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {list.data.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer hover:bg-muted"
                  onClick={() => navigate(`/leads/${r.id}`)}
                >
                  <td className="px-4 py-2.5 font-medium">
                    <Link to={`/leads/${r.id}`} onClick={(e) => e.stopPropagation()}>
                      {r.legalName}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-muted-foreground">
                    {formatClientNumbers(r) || r.country}
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={r.clientStatus} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground max-sm:hidden">
                    {fmt(r.submittedAt)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground max-md:hidden">
                    {r.contractSignedOn ? fmt(r.contractSignedOn) : '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground max-lg:hidden">
                    {r.lastPaymentOn ? fmt(r.lastPaymentOn) : '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
                    {r.commissionToDate ? money(r.commissionToDate) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
